import { useState, useMemo, useEffect, useRef } from "react";
import useLang from "../../../hooks/useLang";
import useAnalysisData from "./useAnalysisData";
import { groupByDestinationAndAspect } from "../utils/newReportUtils";
import ReportWizardFooter from "../ReportWizardFooter";
import Loader from "../../../components/Loader";

const useStepTwo = ({
  analysisId,
  setAnalysisId,
  navigateWizard,
  isNavigating,
}) => {
  const lang = useLang();
  const { analysisState, conclusionsStatus, conclusionsData } = useAnalysisData(
    {
      analysisId,
      setAnalysisId,
    }
  );

  const filteredConclusionIdsRef = useRef();
  const [selectedConclusionOptions, setSelectedConclusionOptions] = useState();
  const isLoading = (status) => status !== "fulfilled" && status !== "rejected";

  const wizardFooterButtons = [
    {
      id: "back",
      caption: lang("common.back"),
      onClick: () => {
        navigateWizard(0);
      },
      disabled: isNavigating,
    },
    {
      id: "generate_report",
      caption: lang("reports.generate_pdf"),
      onClick: () => {
        navigateWizard(2);
      },
      important: true,
      disabled: isNavigating,
    },
  ];

  const getConclusionSections = (groupedOptions) => {
    const flattened = [];
    groupedOptions.forEach((destinationGroup) => {
      const { destination, items: aspects } = destinationGroup;
      aspects.forEach((aspectGroup) => {
        const { aspect, items } = aspectGroup;
        flattened.push({
          header: {
            title:
              "Lentelė " +
              lang(
                `destination.${destination.replace(".", "_").toLowerCase()}`
              ) +
              ". Aspektas: " +
              lang(`conclusions.${aspect}`),
          },
          items: {
            id: `conclusions-${destination}-${aspect}`,
            loading: isLoading(conclusionsStatus),
            noOptionsLabel: lang("captions.no_options"),
            options: items,
            type: "list",
            formItem: true,
            label: null,
            grow: true,
            idAsValue: true,
            value: items
              ?.filter((opt) =>
                selectedConclusionOptions?.value?.includes(opt.id)
              )
              .map((opt) => opt.id),
            onChange: (data) => {
              setSelectedConclusionOptions((prev) => {
                const prevValues = prev?.value ?? [];
                const newValues = data?.value ?? [];
                // Remove old values from this section
                const updated = [
                  // Keep values from other sections
                  ...prevValues.filter(
                    (id) => !items.find((opt) => opt.id === id) // keep IDs not part of this selector
                  ),
                  // Add the newly selected ones from this section
                  ...newValues,
                ];
                return {
                  id: "conclusions",
                  value: [...new Set(updated)], // deduplicate
                };
              });
            },
            disabled: analysisState === "FINISHED",
          },
        });
      });
    });
    return flattened;
  };

  const groupedConclusionOptions = useMemo(
    () => groupByDestinationAndAspect(conclusionsData),
    [conclusionsData]
  );

  const stepTwoArticles = isNavigating
    ? [<Loader small />]
    : [...getConclusionSections(groupedConclusionOptions)];
  if (analysisState !== "FINISHED") {
    stepTwoArticles.push(<ReportWizardFooter buttons={wizardFooterButtons} />);
  }

  const selectedConclusions = conclusionsData?.filter((conclusion) => {
    const found = selectedConclusionOptions?.value?.find(
      (selectedConclusion) => selectedConclusion === conclusion?.id
    );
    return found != null;
  });

  const getSelectedIds = (items = []) =>
    items?.filter((item) => item?.selected).map((item) => item.id);

  useEffect(() => {
    if (conclusionsStatus !== "fulfilled") return;
    if (Array.isArray(conclusionsData) && conclusionsData?.length === 0) {
      navigateWizard(0);
    }
    const selectedConclusionIds = getSelectedIds(conclusionsData);
    setSelectedConclusionOptions({
      id: "conclusions",
      value: selectedConclusionIds,
    });
    filteredConclusionIdsRef.current = selectedConclusionIds;
    // eslint-disable-next-line
  }, [conclusionsStatus]);

  return { stepTwoArticles, selectedConclusionOptions, selectedConclusions };
};

export default useStepTwo;
